import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { BlockBlobClient } from '@azure/storage-blob';
import { SharedService } from '@shared/services/shared.service';
import { IDocument } from 'app/models/documents/document';
import { IDocumentType } from 'app/models/documents/documentType';
import { PublicDocumentService } from 'app/services/document/public-document.service';

@Component({
  selector: 'app-public-document',
  templateUrl: './public-document.component.html',
  styleUrl: './public-document.component.scss'
})
export class PublicDocumentComponent implements OnInit {
  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() types: string[] = ["Other"];
  @Input() canUpload: boolean = true;
  @Input() stageDocuments: boolean = false;
  @Output() uploadsComplete = new EventEmitter<void>();

  public documents: IDocument[] = [];
  public tempFiles: File[] = [];
  public documentTypes: IDocumentType[] = [];
  public selectedTypes: IDocumentType[] = [];
  public includeArchived: boolean = false;

  public downloadProgress: number = 0;
  public uploadProgress: number = 0;
  public uploading: boolean = false;
  public downloading: boolean = false;
  private pendingUploads: number = 0;
  public sasUrl: string = "";

  public sharedService = inject(SharedService);
  public documentService = inject(PublicDocumentService);

  ngOnInit(): void {
    this.getDocumentTypes();
  }

  getDocumentTypes(): void {
    this.documentService.getDocumentTypes(this.types).subscribe({
      next: response => this.documentTypes = response,
      error: e => console.log('Error downloading document types', e),
      complete: () => {
        if(this.types.length > 0){
          this.selectedTypes = this.documentTypes.filter(dt => this.types.includes(dt.type));
        }
        else{
          this.selectedTypes = this.documentTypes.filter(dt => dt.type === 'Other');
        }
      }
    });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFiles(files);
    }
  }

  public onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(input.files);
    }
  }

  handleFiles(files: FileList) {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!this.isValidFileType(file)) {
        this.sharedService.openNotice(file.name + ' is an invalid file type. Please upload an image or document file.');
        continue;
      }
      else if(this.stageDocuments){
        this.tempFiles.push(file);
        this.documents.push({
          id: 0,
          name: file.name,
          azureFile: true,
          extension: '',
          parentId: this.entityId,
          adminOnly: false,
          archived: false,
          types: this.selectedTypes
        });
      }
      else{
        this.uploadFile(file);
      }
    }
  }

  uploadFiles(): void {
    if (this.tempFiles.length === 0) {
      this.uploadsComplete.emit();
      return;
    }

    this.documents = [];
    this.pendingUploads = this.tempFiles.length;

    for(let file of this.tempFiles){
      this.uploadFile(file);
    }
  }

  uploadFile(dirtyFile: File): void {
    this.uploading = true;
    this.uploadProgress = 0;
    const file = new File([dirtyFile], this.generateUniqueFileName(dirtyFile.name.replace(/[^A-Za-z0-9.]/g, '')));
    this.documentService.uploadFile(file).subscribe({
      next: progress => {
        if(progress !== null){
          this.uploadProgress = progress.progress;
        }
      },
      error: err => console.error(err),
      complete: () => {
        let upload: IDocument = {
          id: 0,
          name: file.name,
          azureFile: true,
          extension: '',
          parentId: this.entityId,
          adminOnly: false,
          archived: false,
          types: this.selectedTypes
        };
        this.createDocumentRecord(upload);
      }
    });
  }

  private createDocumentRecord(document: IDocument): void {
    this.documentService.createDocumentRecord(document, this.entityType).subscribe({
      next: response => {
        this.documents.push(response);
        this.pendingUploads--;
        this.checkUploadCompletion();
      },
      error: e => {
        this.sharedService.toastr.error("Could not upload document to database");
        console.log('Error uploading document to database', e);
        this.pendingUploads--;
        this.checkUploadCompletion();
      }
    });
  }

  private checkUploadCompletion(): void {
    if (this.pendingUploads === 0) {
      this.uploading = false;
      this.tempFiles = [];
      this.uploadsComplete.emit();
    }
  }

  generateUniqueFileName(filename: string): string {
    let name = filename.split('.');
    let extension = name.pop();
    let nameWithoutExtension = name.join('.');
    let timestamp = new Date().getTime();
    return nameWithoutExtension + '_' + timestamp + '.' + extension;
  }

  isValidFileType(file: File): boolean {
    const allowedTypes = [
      'image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp',
      'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    return allowedTypes.includes(file.type);
  }
}
